/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jun 09 2023
 **/

import { SyntheticEvent } from "react";

import { filterHTMLAttribute } from "@/frameworks/libraries/utilities";

/**
 * A component that renders an image with the given CardImageProps.
@param src - 이미지 리소스
@param errorSrc - 이미지 출력을 실패할 경우, 다른 이미지 리소스
@param optimize - 이미지 최적화 여부, BE에서 이미지 최적화를 지원하는 경우, true로 설정
 */
export interface ImageCompProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	src: string;
	width?: UnitString;
	height?: UnitString;
	errorSrc?: string;
	optimize?: {
		w: number;
		h: number;
	};
}
export default function Image({ alt, errorSrc, optimize, ...props }: ImageCompProps) {
	const src = optimize && !isDataURL(props.src) ? optimizeImageSrc(props.src, optimize) : props.src;
	const handleError = (evt: SyntheticEvent<HTMLImageElement, Event>) => {
		if (errorSrc) {
			evt.currentTarget.src = errorSrc;
		}

		props.onError?.(evt);
	};

	return (
		<img
			css={{
				width: props.width,
				height: props.height
			}}
			draggable="false"
			{...filterHTMLAttribute(props)}
			src={src}
			alt={alt}
			onError={handleError}
		/>
	);
}

function optimizeImageSrc(src: string, param: { w: number; h: number }) {
	const qs = new URLSearchParams({ w: param.w.toString(), h: param.h.toString() }).toString();
	return `${src}?${qs}`;
}

function isDataURL(str: string) {
	// Base64 문자열이 data:로 시작하는지 확인
	if (str.startsWith("data:")) {
		const regex = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+)?;base64,/;
		return regex.test(str);
	}
	return false;
}
